import { useState, useMemo } from 'react';
import { Search, CheckCircle2, XCircle, AlertTriangle, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AdminTabShell } from './AdminTabShell';
import {
  type JobRunRow,
  type JobRunsData,
  type JobRunStatus,
  classifyRunStatus,
  formatDuration,
} from '@/hooks/admin/useAdminJobRuns';

// ─── Props ──────────────────────────────────────────────────────────────

interface JobRunsTabProps {
  data: JobRunsData | null;
  isLoading: boolean;
  error: Error | null;
}

// ─── Status display config ──────────────────────────────────────────────

const STATUS_COLORS: Record<JobRunStatus, string> = {
  succeeded: 'bg-emerald-900/50 text-emerald-300',
  failed: 'bg-red-900/50 text-red-300',
  partial: 'bg-amber-900/50 text-amber-300',
};

const STATUS_ICONS: Record<JobRunStatus, typeof CheckCircle2> = {
  succeeded: CheckCircle2,
  failed: XCircle,
  partial: AlertTriangle,
};

// ─── Filter type ────────────────────────────────────────────────────────

type RunFilter = 'all' | 'failed' | 'partial' | 'succeeded';

// ─── Detail panel ───────────────────────────────────────────────────────

function JobRunDetailPanel({
  row,
  onClose,
}: {
  row: JobRunRow;
  onClose: () => void;
}) {
  const status = classifyRunStatus(row);
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.job_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5 font-mono">
              Run: {row.run_id}
            </p>
          </div>
          <button onClick={onClose} className="text-wt-text-muted hover:text-wt-text-primary text-lg leading-none">
            &times;
          </button>
        </div>

        {/* Status banner */}
        <div className={`rounded-lg p-3 text-xs ${status === 'succeeded' ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-red-900/20 border border-red-800/40'}`}>
          <div className="flex items-center gap-2">
            <StatusBadge status={status} />
          </div>
          {row.error_message && (
            <p className="mt-2 text-wt-text-secondary font-mono text-[11px] break-all">
              {row.error_message}
            </p>
          )}
        </div>

        {/* Timing */}
        <Section title="Timing">
          <DetailRow label="Started" value={formatTimestamp(row.started_at)} />
          <DetailRow label="Completed" value={formatTimestamp(row.completed_at)} />
          <DetailRow label="Duration" value={formatDuration(row.duration_seconds)} />
        </Section>

        {/* Counts */}
        <Section title="Item Counts">
          <DetailRow label="Processed" value={String(row.rows_processed)} />
          <DetailRow label="Skipped" value={String(row.rows_skipped)} />
          <DetailRow
            label="Failed"
            value={String(row.rows_failed)}
            warn={row.rows_failed > 0}
          />
        </Section>

        {/* Metadata */}
        {row.metadata && Object.keys(row.metadata).length > 0 && (
          <Section title="Metadata">
            {Object.entries(row.metadata).map(([key, val]) => (
              <DetailRow key={key} label={key} value={String(val)} />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-1.5 text-xs">
      <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">{title}</h3>
      {children}
    </div>
  );
}

function DetailRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-wt-text-muted">{label}</span>
      <span className={`font-mono text-right max-w-[60%] truncate ${warn ? 'text-amber-400' : 'text-wt-text-primary'}`}>
        {value}
      </span>
    </div>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────

export function JobRunsTab({ data, isLoading, error }: JobRunsTabProps) {
  const [filter, setFilter] = useState<RunFilter>('all');
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<JobRunRow | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.rows;

    // Status filter
    switch (filter) {
      case 'failed': list = list.filter((r) => !r.success); break;
      case 'partial': list = list.filter((r) => r.success && r.rows_failed > 0); break;
      case 'succeeded': list = list.filter((r) => r.success && r.rows_failed === 0); break;
    }

    // Job name filter
    if (jobFilter !== 'all') {
      list = list.filter((r) => r.job_name === jobFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.job_name.toLowerCase().includes(q) ||
        (r.error_message?.toLowerCase().includes(q) ?? false)
      );
    }

    return list;
  }, [data, filter, jobFilter, search]);

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Runs Today" count={data.summary.runsToday} className="text-wt-text-primary" />
            <SummaryCard label="Failed" count={data.summary.failed} className={data.summary.failed > 0 ? 'text-red-400' : 'text-zinc-500'} />
            <SummaryCard label="Partial" count={data.summary.partial} className={data.summary.partial > 0 ? 'text-amber-400' : 'text-zinc-500'} />
            <SummaryCard label="Avg Duration" count={0} className="text-blue-400" customValue={formatDuration(data.summary.avgDurationSeconds)} />
            <SummaryCard label="Items Failed" count={data.summary.totalItemsFailed} className={data.summary.totalItemsFailed > 0 ? 'text-red-400' : 'text-zinc-500'} />
          </div>

          {/* Filters + search */}
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill label="All" value="all" current={filter} onSelect={setFilter} />
            <FilterPill label="Failed" value="failed" current={filter} onSelect={setFilter} />
            <FilterPill label="Partial" value="partial" current={filter} onSelect={setFilter} />
            <FilterPill label="Succeeded" value="succeeded" current={filter} onSelect={setFilter} />

            {/* Job name dropdown */}
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="h-6 px-2 rounded text-[11px] bg-wt-bg-tertiary text-wt-text-muted border border-wt-border"
            >
              <option value="all">All jobs</option>
              {data.jobNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>

            <div className="ml-auto relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
              <Input
                placeholder="Search job/error..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 pl-7 w-48 text-xs bg-wt-bg-secondary border-wt-border"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-wt-text-muted text-left border-b border-wt-border">
                  <th className="pb-2 pr-3 font-medium">Job</th>
                  <th className="pb-2 pr-3 font-medium">Status</th>
                  <th className="pb-2 pr-3 font-medium">Started</th>
                  <th className="pb-2 pr-3 font-medium text-right">Duration</th>
                  <th className="pb-2 pr-3 font-medium text-right">Processed</th>
                  <th className="pb-2 pr-3 font-medium text-right">Skipped</th>
                  <th className="pb-2 pr-3 font-medium text-right">Failed</th>
                  <th className="pb-2 pr-3 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const status = classifyRunStatus(row);
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-wt-border hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedRow(row)}
                    >
                      <td className="py-2 pr-3">
                        <span className="text-wt-text-primary font-mono text-[11px]">
                          {row.job_name}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge status={status} />
                      </td>
                      <td className="py-2 pr-3 text-wt-text-secondary font-mono">
                        {formatRelativeTime(row.started_at)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-wt-text-secondary">
                        {formatDuration(row.duration_seconds)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-wt-text-primary">
                        {row.rows_processed}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-wt-text-muted">
                        {row.rows_skipped}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        <span className={row.rows_failed > 0 ? 'text-red-400' : 'text-wt-text-muted'}>
                          {row.rows_failed}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-wt-text-muted truncate max-w-[180px]">
                        {row.error_message?.slice(0, 60) ?? '—'}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-wt-text-muted">
                      No job runs match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedRow && (
        <JobRunDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </AdminTabShell>
  );
}

// ─── Small components ───────────────────────────────────────────────────

function SummaryCard({ label, count, className, customValue }: { label: string; count: number; className?: string; customValue?: string }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 border border-wt-border">
      <p className="text-[11px] text-wt-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${className ?? 'text-wt-text-primary'}`}>
        {customValue ?? count}
      </p>
    </div>
  );
}

function FilterPill<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: JobRunStatus }) {
  const Icon = STATUS_ICONS[status];
  const labels: Record<JobRunStatus, string> = {
    succeeded: 'Succeeded',
    failed: 'Failed',
    partial: 'Partial',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[status]}`}>
      <Icon className="h-2.5 w-2.5" />
      {labels[status]}
    </span>
  );
}

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}
